import { FC, ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import { I18nextProvider, useTranslation } from 'react-i18next';
import { ProposalData } from 'ts/shared/interfaces/ProposalData';
import Rating from 'components/ui/Rating/Rating';
import Tooltip from 'components/ui/Tooltip/Tooltip';
import { showError, showWarning } from 'utils/utils';
import { i18nTB } from 'utils/shared/utils';
import {
  TripPreviewActiveState,
  TripPreviewFlightCard,
  TripPreviewHotelCard,
  TripPreviewTransferCard,
  TripPreviewSection,
  TripPreviewSlotView,
  buildTripPreviewInputData,
  buildTripPreviewModel,
  resolveTripPreviewActiveFromInputData,
  parseTripPreviewProposalImageFromInputData,
  parseTripPreviewHotelImagesFromInputData,
  resolveLanguageFromProposal,
} from 'utils/shared/tripResultBuilderPreview';
import TripBuilderClientSelection, { buildMandatoryProgress } from './TripBuilderClientSelection';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown, faChevronUp, faVanShuttle } from '@fortawesome/free-solid-svg-icons';
import { useSearchParams } from 'react-router-dom';
import TripBuilderClientRoute from './TripBuilderClientRoute';
import { parseHotelInfoConfigs, resolveHotelInfo, resolveHotelInfoConfig } from 'utils/shared/hotelProposalInformation';
import { parseClaudeHotelInfoConfigs, resolveClaudeHotelInfoConfig } from 'utils/shared/claudeProposalInformation';
import SingleProposalStyles from '../Single/SingleProposalStyles';
import ProposalHtmlGoogle from '../Single/ProposalHtlGoogle';
import ProposalHtmlTripadvisor from '../Single/ProposalHtlTripadvisor';
import ProposalHtmlHotelInformation from '../Single/ProposalHtlHotelInformation';
import ProposalHtlClaudeHotelInformation from '../Single/ProposalHtlClaudeHotelInformation';
import { getBlobUrl, resolveGalleryImageUrl } from 'utils/shared/tripBuilderImages';
import { resolveTripBuilderClientUiVersion } from 'utils/shared/tripBuilderClientSummary';
import TripBuilderClientHeader2 from './TripBuilderClientHeader2';
import TripBuilderClientItinerary2 from './TripBuilderClientItinerary2';
import TripBuilderClientFlights2 from './TripBuilderClientFlights2';
import TripBuilderClientDestinationHotels2 from './TripBuilderClientDestinationHotels2';
import TripBuilderClientTestimonials2 from './TripBuilderClientTestimonials2';
import TripBuilderClientWhyFlyeast2 from './TripBuilderClientWhyFlyeast2';
import TripBuilderClientFooter2 from './TripBuilderClientFooter2';
import TripBuilderGetPdfModal from './TripBuilderGetPdfModal';
import TripBuilderClientHotelsCta2 from './TripBuilderClientHotelsCta2';
import { hasTripBuilderSavedPassword, isTripBuilderUnlocked } from 'utils/shared/tripBuilder';
import TripBuilderClientPasswordLock from './TripBuilderClientPasswordLock';
import { imgBg, imgLogo } from 'constants/generic';

interface TripBuilderPageProps {
  proposal: ProposalData;
  onProposalUpdated?: (proposal: ProposalData) => void;
}

const getCompoundFlightBackground = (color: string) => `color-mix(in srgb, ${color} 20%, white)`;

const clearRoundTripFlightKeys = (
  active: TripPreviewActiveState,
  rootSlotKey: string,
  slot: TripPreviewSlotView,
): TripPreviewActiveState => {
  const next = {
    activeBefore: { ...active.activeBefore },
    activeAfter: { ...active.activeAfter },
    activeMiddle: { ...active.activeMiddle },
    activeHotel: { ...active.activeHotel },
    activeTransfer: { ...active.activeTransfer },
  };

  const beforeKey = next.activeBefore[rootSlotKey];
  if (
    beforeKey &&
    slot.beforeSection?.flights.some((flight) => flight.key === beforeKey && flight.isRoundTrip)
  ) {
    delete next.activeBefore[rootSlotKey];
  }

  const afterKey = next.activeAfter[rootSlotKey];
  if (
    afterKey &&
    slot.afterSection?.flights.some((flight) => flight.key === afterKey && flight.isRoundTrip)
  ) {
    delete next.activeAfter[rootSlotKey];
  }

  slot.chainNodes.forEach((node) => {
    node.middleSections.forEach((section) => {
      if (!section.transitionKey) {
        return;
      }
      const middleKey = next.activeMiddle[section.transitionKey];
      if (
        middleKey &&
        section.flights.some((flight) => flight.key === middleKey && flight.isRoundTrip)
      ) {
        delete next.activeMiddle[section.transitionKey];
      }
    });
  });

  return next;
};

const syncRoundTripFlight = (
  active: TripPreviewActiveState,
  slot: TripPreviewSlotView,
  rootSlotKey: string,
  flightKey: string,
): TripPreviewActiveState => {
  const next = {
    ...active,
    activeBefore: { ...active.activeBefore, [rootSlotKey]: flightKey },
    activeAfter: { ...active.activeAfter },
    activeMiddle: { ...active.activeMiddle },
    activeTransfer: { ...active.activeTransfer },
  };

  if (slot.afterSection?.flights.some((flight) => flight.key === flightKey)) {
    next.activeAfter[rootSlotKey] = flightKey;
  }

  slot.chainNodes.forEach((node) => {
    node.middleSections.forEach((section) => {
      if (section.transitionKey && section.flights.some((flight) => flight.key === flightKey)) {
        next.activeMiddle[section.transitionKey] = flightKey;
      }
    });
  });

  return next;
};

const clearFlightKeyFromActive = (
  active: TripPreviewActiveState,
  slot: TripPreviewSlotView,
  flightKey: string,
): TripPreviewActiveState => {
  const next = {
    activeBefore: { ...active.activeBefore },
    activeAfter: { ...active.activeAfter },
    activeMiddle: { ...active.activeMiddle },
    activeHotel: { ...active.activeHotel },
    activeTransfer: { ...active.activeTransfer },
  };

  if (next.activeBefore[slot.rootSlotKey] === flightKey) {
    delete next.activeBefore[slot.rootSlotKey];
  }
  if (next.activeAfter[slot.rootSlotKey] === flightKey) {
    delete next.activeAfter[slot.rootSlotKey];
  }
  slot.chainNodes.forEach((node) => {
    node.middleSections.forEach((section) => {
      if (section.transitionKey && next.activeMiddle[section.transitionKey] === flightKey) {
        delete next.activeMiddle[section.transitionKey];
      }
    });
  });

  return next;
};

const TripPreviewOptionRow: FC<{
  section: TripPreviewSection;
  children: ReactNode;
}> = ({ children }) => <div className="tb-option-row-content">{children}</div>;

const TripPreviewFlightCardView: FC<{
  card: TripPreviewFlightCard;
  onSelect: () => void;
  hidePrices?: boolean;
  canExpand?: boolean;
  expanded?: boolean;
  onToggleExpand?: () => void;
}> = ({ card, onSelect, hidePrices, canExpand, expanded, onToggleExpand }) => {
  const { t } = useTranslation(undefined, { i18n: i18nTB });
  const [fromCode, toCode] = card.route.split('->').map((item) => item.trim());
  const departureTime = card.departureTime || (card.scheduleLabel || '').split('-')[0]?.trim().split(' ').pop() || '-';
  const arrivalTime =
    card.arrivalTime || (card.scheduleLabel || '').split('-')[1]?.trim().split(' ')[0] || '-';

  return (
    <div
      className={
        'tb-flight-option-card flex-between' +
        (card.isActive ? ' tb-flight-option-card-active' : '') +
        (card.compoundColor ? ' tb-flight-option-card-compound' : '')
      }
      style={
        card.compoundColor ? { backgroundColor: getCompoundFlightBackground(card.compoundColor) } : undefined
      }
      onClick={onSelect}
      role="button"
      tabIndex={0}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          onSelect();
        }
      }}
    >
      <div className="tb-flight-option-left flex-v-center">
        <div className="tb-flight-option-main flex-column">
          <div className="tb-flight-option-row-labels flex-between text-xsm text-bold">
            <span>{t('departure', { label: card.departureLabel || '-' })}</span>
            <span>{t('arrival', { label: card.arrivalLabel || '-' })}</span>
          </div>
          <div className="tb-flight-option-row-codes text-slg text-bold">
            <span>{fromCode || '-'}</span>
            <span className="tb-flight-option-arrow text-sm">
              <span className="tb-flight-option-arrow-icon" aria-hidden="true">
                ✈
              </span>
              <span className="tb-flight-option-arrow-line" aria-hidden="true" />
              {!!card.durationLabel && <span className="tb-flight-option-duration">{card.durationLabel}</span>}
            </span>
            <span>{toCode || '-'}</span>
          </div>
          <div className="tb-flight-option-row-times flex-between text-slg text-bold">
            <span>{departureTime}</span>
            <span>{arrivalTime}</span>
          </div>
          {(!!card.flightInfoLabel || !!card.arrivalDetailLabel) && (
            <div className="tb-flight-option-row-meta text-xsm">
              {!!card.flightInfoLabel && <span>{card.flightInfoLabel}</span>}
              {!!card.arrivalDetailLabel && <span>{card.arrivalDetailLabel}</span>}
            </div>
          )}
        </div>
      </div>
      <div className="tb-flight-option-right flex-column">
        {!hidePrices && !!card.priceLabel && (
          <span className="tb-flight-option-price text-slg text-bold text-black">{card.priceLabel}</span>
        )}
        {!!canExpand && (
          <button
            type="button"
            className="tb-flight-option-expand-btn"
            onClick={(event) => {
              event.stopPropagation();
              onToggleExpand?.();
            }}
          >
            <FontAwesomeIcon
              icon={expanded ? faChevronUp : faChevronDown}
              className="tb-flight-option-expand-caret"
            />
          </button>
        )}
      </div>
    </div>
  );
};

const TripPreviewHotelCardView: FC<{
  card: TripPreviewHotelCard;
  index: number;
  onSelect: () => void;
  onSelectRoom: (roomIndex: number) => void;
  selectedRoomIndex: number;
  hidePrices?: boolean;
  externalParam_1?: string;
}> = ({ card, index, onSelect, onSelectRoom, selectedRoomIndex, hidePrices, externalParam_1 }) => {
  const { t } = useTranslation(undefined, { i18n: i18nTB });
  const blobUrl = getBlobUrl();
  const hotelImageSrc = card.logoCode
    ? resolveGalleryImageUrl(blobUrl, card.logoCode)
    : imgBg;
  const [detailsExpanded, setDetailsExpanded] = useState(false);
  const detailsPanelRef = useRef<HTMLDivElement>(null);
  const hasMultipleRooms = (card.rooms || []).length > 1;
  const selectedRoom = (card.rooms || [])[selectedRoomIndex];
  const displayPrice =
    selectedRoom?.priceLabel ?? (card.priceLabel ? card.priceLabel.replace(/^From\s+/i, '') : null);
  const hotelInfoConfig = resolveHotelInfoConfig(
    parseHotelInfoConfigs(externalParam_1),
    { item: { itemCode: card.itemCode }, itemCode: card.itemCode },
    card.hotelIndex ?? 0,
  );
  const claudeHotelInfoConfig = resolveClaudeHotelInfoConfig(
    parseClaudeHotelInfoConfigs(externalParam_1),
    { hotel: { code: card.itemCode } },
    card.hotelIndex ?? 0,
  );
  const hasGoogleTripadvisor = !!card.serpHotel || !!card.tripAdvisorHotel;
  const hasHotelInfo =
    !!resolveHotelInfo(card.hotelInformatiom) &&
    !!hotelInfoConfig &&
    !!(hotelInfoConfig.details || hotelInfoConfig.overview || hotelInfoConfig.imagesIndexes?.length);
  const hasAiHotelInfo =
    !!card.aI_HotelInformatiom?.content?.length &&
    !!claudeHotelInfoConfig &&
    !!(
      claudeHotelInfoConfig.details ||
      claudeHotelInfoConfig.overview ||
      claudeHotelInfoConfig.prosCons ||
      claudeHotelInfoConfig.imagesIndexes?.length
    );
  const hasDetails = hasGoogleTripadvisor || hasHotelInfo || hasAiHotelInfo;

  useEffect(() => {
    const panel = detailsPanelRef.current;
    if (!panel) return;
    panel.style.maxHeight = detailsExpanded ? `${panel.scrollHeight}px` : '';
  }, [detailsExpanded]);

  return (
    <div
      className={
        'tb-hotel-option-card flex-between' + (card.isActive ? ' tb-hotel-option-card-active' : '')
      }
      onClick={onSelect}
      role="button"
      tabIndex={0}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          onSelect();
        }
      }}
    >
      <img className="tb-hotel-option-card-img" src={hotelImageSrc} alt="" onError={(e) => { e.currentTarget.src = imgBg; }} />
      <div className="tb-hotel-option-card-left flex-column">
        <div className="tb-hotel-option-card-head flex-v-center">
          <span className={'tb-hotel-option-checkbox' + (card.isActive ? ' tb-hotel-option-checkbox-active' : '')} />
          <span className="tb-hotel-option-badge text-xxs text-bold">
            {t('option', { letter: String.fromCharCode(65 + index) })}
          </span>
        </div>
        <Tooltip content={card.name} noPlague>
          <div className="tb-hotel-option-name text-slg text-bold">{card.name}</div>
        </Tooltip>
        {!!card.star && <Rating value={card.star} className="tb-hotel-option-rating" />}
        {(card.rooms || []).some((room) => room.roomLabel || room.boardLabel || room.cxlLabel) && (
          <div className="tb-hotel-option-tags flex-row">
            {(card.rooms || []).map((room, roomIndex) => (
              <div
                key={roomIndex}
                className={
                  'tb-hotel-option-room-row' +
                  (hasMultipleRooms ? ' tb-hotel-option-room-row-clickable' : '') +
                  (hasMultipleRooms && selectedRoomIndex === roomIndex
                    ? ' tb-hotel-option-room-row-selected'
                    : '')
                }
                onClick={(event) => {
                  event.stopPropagation();
                  if (hasMultipleRooms) {
                    onSelectRoom(roomIndex);
                  }
                }}
              >
                {hasMultipleRooms && (
                  <span
                    className={
                      'tb-hotel-option-checkbox' +
                      (selectedRoomIndex === roomIndex ? ' tb-hotel-option-checkbox-active' : '')
                    }
                  />
                )}
                {!!room.roomLabel && (
                  <span className="tb-hotel-option-tag tb-hotel-option-tag-room text-xxs">{room.roomLabel}</span>
                )}
                {!!room.boardLabel && (
                  <span className="tb-hotel-option-tag tb-hotel-option-tag-board text-cap text-xxs">{room.boardLabel}</span>
                )}
                {!!room.cxlLabel && room.cxlType === 'full' && (
                  <span className="tb-hotel-option-tag tb-hotel-option-tag-cxl text-xxs" style={{ background: 'var(--color-danger)', color: '#fff' }}>
                    {room.cxlLabel}
                  </span>
                )}
                {!!room.cxlLabel && room.cxlType === 'free' && (
                  <span className="tb-hotel-option-tag tb-hotel-option-tag-cxl text-xxs" style={{ background: 'var(--color-success)', color: '#fff' }}>
                    {room.cxlLabel}
                  </span>
                )}
              </div>
            ))}
          </div>
        )}
        {!!card.nights && (
          <div className="tb-hotel-option-nights text-sm">
            {card.nights} {card.nights > 1 ? t('nights') : t('night')}
          </div>
        )}

      </div>
      <div className="tb-hotel-option-card-right flex-column">
        {!hidePrices && !!displayPrice && (
          <div className="tb-hotel-option-price text-slg text-bold">{displayPrice}</div>
        )}
        {!hidePrices && <div className="tb-hotel-option-price-sub text-sm">{t('total_stay')}</div>}
      </div>
      {hasDetails && (
        <div className="tb-hotel-option-details text-sm text-bold pt-10">

          <div className="w100p" onClick={(event) => event.stopPropagation()}>
            <button
              type="button"
              className="btn btn-default btn-xsm"
              onClick={() => setDetailsExpanded((prev) => !prev)}
            >
              {t('details_and_photos')}
              <FontAwesomeIcon
                icon={detailsExpanded ? faChevronUp : faChevronDown}
                className="ml-5"
              />
            </button>
          </div>

          <div className="panel" ref={detailsPanelRef} style={{ padding: 0 }}>
            <SingleProposalStyles />
            {hasGoogleTripadvisor && (
              <>
                <ProposalHtmlGoogle serpHotel={card.serpHotel} />
                <ProposalHtmlTripadvisor tripAdvisorHotel={card.tripAdvisorHotel} hideWrapper />
              </>
            )}
            {hasHotelInfo && (
              <ProposalHtmlHotelInformation
                hotelInformation={card.hotelInformatiom}
                config={hotelInfoConfig}
              />
            )}
            {hasAiHotelInfo && (
              <ProposalHtlClaudeHotelInformation
                aI_HotelInformation={card.aI_HotelInformatiom}
                config={claudeHotelInfoConfig}
              />
            )}
          </div>
        </div>
      )}
    </div>
  )
};

const TripPreviewFlightsRow: FC<{
  flights: TripPreviewFlightCard[];
  title?: string;
  hidePrices?: boolean;
  onSelectFlight: (flightKey: string, isRoundTrip: boolean) => void;
}> = ({ flights, title, hidePrices, onSelectFlight }) => {
  const [expanded, setExpanded] = useState(false);
  const displayCard = flights.find((flight) => flight.isActive) || flights[0];
  const restCards = flights.filter((flight) => flight.key !== displayCard?.key);

  if (!displayCard) {
    return null;
  }

  return (
    <>
      {!!title && <div className="tb-flight-row-title">{title}</div>}
      <div className="tb-flight-single-row">
        <TripPreviewFlightCardView
          key={displayCard.key}
          card={displayCard}
          hidePrices={hidePrices}
          onSelect={() => onSelectFlight(displayCard.key, displayCard.isRoundTrip)}
          canExpand={restCards.length > 0}
          expanded={expanded}
          onToggleExpand={() => setExpanded((prev) => !prev)}
        />
        {!!expanded && !!restCards.length && (
          <div className="tb-flight-expanded-list flex-column">
            {restCards.map((card) => (
              <TripPreviewFlightCardView
                key={card.key}
                card={card}
                hidePrices={hidePrices}
                onSelect={() => {
                  onSelectFlight(card.key, card.isRoundTrip);
                  setExpanded(false);
                }}
              />
            ))}
          </div>
        )}
      </div>
    </>
  );
};

const TripPreviewHotelSlotImage: FC<{
  imageUrl?: string;
  imgClassName?: string;
}> = ({ imageUrl, imgClassName = 'tb-hotel-group-toggle-img' }) => {
  const blobUrl = getBlobUrl();

  return imageUrl ? (
    <img className={imgClassName} src={resolveGalleryImageUrl(blobUrl, imageUrl)} alt="" />
  ) : null;
};

const TripPreviewHotelsRow: FC<{
  hotels: TripPreviewHotelCard[];
  hidePrices?: boolean;
  onSelectHotel: (hotelKey: string) => void;
  onSelectRoom: (hotelKey: string, roomIndex: number) => void;
  selectedRoomByHotel: Record<string, number>;
  externalParam_1?: string;
}> = ({ hotels, hidePrices, onSelectHotel, onSelectRoom, selectedRoomByHotel, externalParam_1 }) => (
  <div className="tb-hotels-list flex-column">
    {hotels.map((card, index) => (
      <TripPreviewHotelCardView
        key={card.key}
        card={card}
        index={index}
        hidePrices={hidePrices}
        selectedRoomIndex={selectedRoomByHotel[card.key] ?? 0}
        externalParam_1={externalParam_1}
        onSelect={() => onSelectHotel(card.key)}
        onSelectRoom={(roomIndex) => onSelectRoom(card.key, roomIndex)}
      />
    ))}
  </div>
);

const TripPreviewHotelGroupToggleView: FC<{
  city: string;
  dateLabel: string;
  destinationIndex: number;
  hotelCards: TripPreviewHotelCard[];
  activeHotelKey?: string;
  expanded: boolean;
  slotImageUrl?: string;
  onToggle: () => void;
}> = ({ city, dateLabel, destinationIndex, hotelCards, activeHotelKey, expanded, slotImageUrl, onToggle }) => {
  const { t } = useTranslation(undefined, { i18n: i18nTB });
  const selected = hotelCards.find((h) => h.key === activeHotelKey);

  return (
    <button
      type="button"
      className={
        'tb-hotel-group-toggle flex-between' +
        (selected ? ' tb-hotel-group-toggle-active' : '') +
        (expanded ? ' tb-hotel-group-toggle-open' : '')
      }
      onClick={onToggle}
    >
      <div className="tb-hotel-group-toggle-left flex-v-center">
        <TripPreviewHotelSlotImage imageUrl={slotImageUrl} />
        <div className="tb-hotel-group-toggle-text flex-column">
          <div className="tb-hotel-group-toggle-city text-slg text-bold">
            <span className="tb-hotel-group-toggle-index">{destinationIndex}</span>
            {city}
          </div>
          <div className="tb-hotel-group-toggle-meta text-sm">{dateLabel}</div>
        </div>
      </div>

      <div className="tb-hotel-group-toggle-right flex-v-center">
        <div className="tb-hotel-group-toggle-selection text-sm text-bold">
          {selected ? selected.name : t('no_hotel_selected')}
        </div>
        <FontAwesomeIcon
          icon={expanded ? faChevronUp : faChevronDown}
          className="tb-hotel-group-toggle-caret"
        />
      </div>
    </button>
  );
};

const TripPreviewTransferOptionCardView: FC<{
  card: TripPreviewTransferCard;
  index: number;
  onSelect: () => void;
  hidePrices?: boolean;
}> = ({ card, index, onSelect, hidePrices }) => {
  const { t } = useTranslation(undefined, { i18n: i18nTB });
  const routeLabel = [card.pickUpLabel, card.dropOffLabel].filter(Boolean).join(' → ');

  return (
    <button
      type="button"
      className={'tb-hotel-option-card flex-between' + (card.isActive ? ' tb-hotel-option-card-active' : '')}
      onClick={onSelect}
    >
      <div className="tb-hotel-option-card-left flex-column">
        <div className="tb-hotel-option-card-head flex-v-center">
          <span
            className={'tb-hotel-option-checkbox' + (card.isActive ? ' tb-hotel-option-checkbox-active' : '')}
          />
          <span className="tb-hotel-option-badge text-xxs text-bold">
            {t('option', { letter: String.fromCharCode(65 + index) })}
          </span>
        </div>
        <Tooltip content={card.name} noPlague>
          <div className="tb-hotel-option-name text-slg text-bold">{card.name}</div>
        </Tooltip>
        {!!routeLabel && <div>{routeLabel}</div>}
        {!!card.scheduleLabel && <div className="tb-hotel-option-nights text-sm">{card.scheduleLabel}</div>}
      </div>
      <div className="tb-hotel-option-card-right flex-column">
        {!hidePrices && !!card.priceLabel && (
          <div className="tb-hotel-option-price text-slg text-bold">{card.priceLabel}</div>
        )}
      </div>
    </button>
  );
};

const TripPreviewTransferGroupToggleView: FC<{
  title: string;
  transferCards: TripPreviewTransferCard[];
  activeTransferKey?: string;
  expanded: boolean;
  emptySelectionLabel: string;
  onToggle: () => void;
}> = ({ title, transferCards, activeTransferKey, expanded, emptySelectionLabel, onToggle }) => {
  const selected = transferCards.find((card) => card.key === activeTransferKey);
  const selectionLabel = selected
    ? `${selected.name}${selected.scheduleLabel ? ` · ${selected.scheduleLabel}` : ''}`
    : emptySelectionLabel;

  return (
    <button
      type="button"
      className={
        'tb-hotel-group-toggle flex-between' +
        (selected ? ' tb-hotel-group-toggle-active' : '') +
        (expanded ? ' tb-hotel-group-toggle-open' : '')
      }
      onClick={onToggle}
    >
      <div className="tb-hotel-group-toggle-left flex-v-center">
        <div className="tb-hotel-group-toggle-img tb-hotel-group-transfer-icon">
          <FontAwesomeIcon icon={faVanShuttle} />
        </div>
        <div className="tb-hotel-group-toggle-text flex-column">
          <div className="tb-hotel-group-toggle-city text-slg text-bold">{title}</div>
        </div>
      </div>
      <div className="tb-hotel-group-toggle-right flex-v-center">
        <div
          className={
            'tb-hotel-group-toggle-selection' +
            (!selected ? ' tb-hotel-group-transfer-selection-empty' : '')
          }
        >
          {selectionLabel}
        </div>
        <span
          className={
            'tb-hotel-group-toggle-caret' + (expanded ? ' tb-hotel-group-toggle-caret-open' : '')
          }
        />
      </div>
    </button>
  );
};

const TripPreviewTransfersGroup: FC<{
  title: string;
  selectTitle: string;
  emptySelectionLabel: string;
  transfers: TripPreviewTransferCard[];
  activeTransferKey?: string;
  expanded: boolean;
  onToggle: () => void;
  hidePrices?: boolean;
  onSelectTransfer: (transferKey: string) => void;
}> = ({
  title,
  selectTitle,
  emptySelectionLabel,
  transfers,
  activeTransferKey,
  expanded,
  onToggle,
  hidePrices,
  onSelectTransfer,
}) => (
    <div className={'tb-hotel-group tb-hotel-group-transfer' + (expanded ? ' tb-hotel-group-open' : '')}>
      <TripPreviewTransferGroupToggleView
        title={title}
        transferCards={transfers}
        activeTransferKey={activeTransferKey}
        expanded={expanded}
        emptySelectionLabel={emptySelectionLabel}
        onToggle={onToggle}
      />
      {!!expanded && (
        <div className="tb-hotel-group-content">
          <div className="tb-hotel-group-title-row flex-v-center">
            <span className="tb-hotel-group-title text-sm text-bold">{selectTitle}</span>
          </div>
          <div className="tb-hotels-list flex-column">
            {transfers.map((card, index) => (
              <TripPreviewTransferOptionCardView
                key={card.key}
                card={card}
                index={index}
                hidePrices={hidePrices}
                onSelect={() => onSelectTransfer(card.key)}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );

const TripBuilderPage: FC<TripBuilderPageProps> = (props) => {
  const [urlParams] = useSearchParams();
  const proposalId = urlParams.get('id') || `${props.proposal?.id}` || '';
  const [isUnlocked, setIsUnlocked] = useState(() => isTripBuilderUnlocked(proposalId));

  if (hasTripBuilderSavedPassword(props.proposal) && !isUnlocked) {
    return (
      <TripBuilderClientPasswordLock
        proposal={props.proposal}
        proposalId={proposalId}
        onUnlock={() => setIsUnlocked(true)}
      />
    );
  }

  return <TripBuilderClientView {...props} />;
};

const TripBuilderClientView: FC<TripBuilderPageProps> = ({ proposal, onProposalUpdated }) => {
  const [urlParams] = useSearchParams();
  const lang = resolveLanguageFromProposal(proposal.externalParam_1);
  const { t, i18n } = useTranslation(undefined, { i18n: i18nTB });
  const [active, setActive] = useState<TripPreviewActiveState>(() =>
    resolveTripPreviewActiveFromInputData(proposal),
  );
  const [saving, setSaving] = useState(false);
  const [expandedHotelGroups, setExpandedHotelGroups] = useState<Record<string, boolean>>({});
  const [expandedTransferGroups, setExpandedTransferGroups] = useState<Record<string, boolean>>({});
  const [selectedRoomByHotel, setSelectedRoomByHotel] = useState<Record<string, number>>({});

  const uiVersion = resolveTripBuilderClientUiVersion(
    Number(proposal.companyId) || 0,
  );
  const isUiV2 = uiVersion === 2;

  useEffect(() => {
    i18n.changeLanguage(lang);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setActive(resolveTripPreviewActiveFromInputData(proposal));
    setExpandedHotelGroups({});
    setExpandedTransferGroups({});
    // eslint-disable-next-line react-hooks/exhaustive-deps
    setSelectedRoomByHotel({});
  }, [proposal]);

  const model = useMemo(
    () => buildTripPreviewModel(proposal, active, { selectedRoomByHotel }),
    [proposal, active, selectedRoomByHotel],
  );

  const hotelImageBySlot = useMemo(
    () => parseTripPreviewHotelImagesFromInputData(proposal),
    [proposal],
  );

  const proposalImage = useMemo(
    () => parseTripPreviewProposalImageFromInputData(proposal),
    [proposal],
  );

  const onClickSaveScheme = async () => {
    const isRequired = (label?: string) => (label || '').toLowerCase() !== 'optional';
    const hasMissingMandatorySelection = model.slots.some((slot) => {
      if (
        slot.beforeSection &&
        isRequired(slot.beforeSection.requirementLabel) &&
        !active.activeBefore[slot.rootSlotKey]
      ) {
        return true;
      }
      if (
        slot.afterSection &&
        isRequired(slot.afterSection.requirementLabel) &&
        !active.activeAfter[slot.rootSlotKey]
      ) {
        return true;
      }
      for (const node of slot.chainNodes) {
        if (
          node.hotelSection &&
          isRequired(node.hotelSection.requirementLabel) &&
          !active.activeHotel[node.slotKey]
        ) {
          return true;
        }
        if (
          node.transferSection &&
          isRequired(node.transferSection.requirementLabel) &&
          !active.activeTransfer[node.slotKey]
        ) {
          return true;
        }
        for (const middleSection of node.middleSections) {
          if (
            middleSection.transitionKey &&
            isRequired(middleSection.requirementLabel) &&
            !active.activeMiddle[middleSection.transitionKey]
          ) {
            return true;
          }
        }
      }
      return false;
    });

    if (hasMissingMandatorySelection) {
      showWarning(t('select_mandatory_warning'));
      return;
    }

    setSaving(true);
    try {
      buildTripPreviewInputData(proposal, active);

      //query

    } catch {
      showError(t('something_went_wrong'));
    } finally {
      setSaving(false);
    }
  };

  const selectBeforeFlight = (slot: TripPreviewSlotView, flightKey: string, isRoundTrip: boolean) => {
    setActive((prev) => {
      if (prev.activeBefore[slot.rootSlotKey] === flightKey) {
        return clearFlightKeyFromActive(prev, slot, flightKey);
      }
      let next = isRoundTrip ? prev : clearRoundTripFlightKeys(prev, slot.rootSlotKey, slot);
      if (isRoundTrip) {
        return syncRoundTripFlight(next, slot, slot.rootSlotKey, flightKey);
      }
      return {
        ...next,
        activeBefore: { ...next.activeBefore, [slot.rootSlotKey]: flightKey },
      };
    });
  };

  const selectAfterFlight = (slot: TripPreviewSlotView, flightKey: string, isRoundTrip: boolean) => {
    setActive((prev) => {
      if (prev.activeAfter[slot.rootSlotKey] === flightKey) {
        return clearFlightKeyFromActive(prev, slot, flightKey);
      }
      let next = isRoundTrip ? prev : clearRoundTripFlightKeys(prev, slot.rootSlotKey, slot);
      if (isRoundTrip) {
        return syncRoundTripFlight(next, slot, slot.rootSlotKey, flightKey);
      }
      return {
        ...next,
        activeAfter: { ...next.activeAfter, [slot.rootSlotKey]: flightKey },
      };
    });
  };

  const selectMiddleFlight = (
    slot: TripPreviewSlotView,
    transitionKey: string,
    flightKey: string,
    isRoundTrip: boolean,
  ) => {
    setActive((prev) => {
      if (prev.activeMiddle[transitionKey] === flightKey) {
        return clearFlightKeyFromActive(prev, slot, flightKey);
      }
      if (isRoundTrip) {
        return syncRoundTripFlight(prev, slot, slot.rootSlotKey, flightKey);
      }
      return {
        ...prev,
        activeMiddle: { ...prev.activeMiddle, [transitionKey]: flightKey },
      };
    });
  };

  const selectHotel = (slotKey: string, hotelKey: string) => {
    setActive((prev) => {
      if (prev.activeHotel[slotKey] === hotelKey) {
        const next = { ...prev.activeHotel };
        delete next[slotKey];
        return {
          ...prev,
          activeHotel: next,
        };
      }
      return {
        ...prev,
        activeHotel: { ...prev.activeHotel, [slotKey]: hotelKey },
      };
    });
  };

  const selectHotelRoom = (hotelKey: string, roomIndex: number) => {
    setSelectedRoomByHotel((prev) => ({
      ...prev,
      [hotelKey]: roomIndex,
    }));
  };

  const selectTransfer = (slotKey: string, transferKey: string) => {
    setActive((prev) => {
      if (prev.activeTransfer[slotKey] === transferKey) {
        const next = { ...prev.activeTransfer };
        delete next[slotKey];
        return {
          ...prev,
          activeTransfer: next,
        };
      }
      return {
        ...prev,
        activeTransfer: { ...prev.activeTransfer, [slotKey]: transferKey },
      };
    });
  };

  const hidePrices = model.displayTotalDelta;
  const contentRef = useRef<HTMLDivElement>(null);
  const itineraryRef = useRef<HTMLDivElement>(null);
  const selectionProgress = useMemo(() => buildMandatoryProgress(model.slots), [model.slots]);
  const isSelectionComplete = selectionProgress.percent === 100;
  const [isPdfModalOpen, setIsPdfModalOpen] = useState(false);
  const proposalId = urlParams.get('id2') || `${proposal?.id}` || '';

  return (
    <I18nextProvider i18n={i18nTB}>
      <div className={`tb-wr tb-wr-preview pr-trip-builder tb-client-column tb-client-ui-${isUiV2 ? 2 : 1}`} dir={i18n.language === 'he' ? 'rtl' : 'ltr'}>
        <TripBuilderClientHeader2
          proposal={proposal}
          proposalImage={proposalImage}
          scrollTargetRef={isUiV2 ? itineraryRef : contentRef}
          logoSrc={isUiV2 ? undefined : imgLogo}
          logoAlt={isUiV2 ? 'flyeast' : 'BePro'}
        />
        {isUiV2 && (
          <div ref={itineraryRef}>
            <TripBuilderClientItinerary2 slots={model.slots} />
          </div>
        )}

        <div
          ref={contentRef}
          className={isUiV2 ? 'tb2-client-body' : 'tb-client-layout'}
        >
          <div className={isUiV2 ? 'tb2-client-content' : 'tb-client-main mt-20'}>
            <div className={isUiV2 ? undefined : 'tb-client-column-body'}>
              {!model.slots.length && <div className="tb-empty">{t('no_hotels_in_proposal')}</div>}

              {!!model.slots.length && (
                <>
                  <div className={isUiV2 ? undefined : 'tb-client-panel'}>
                    {!isUiV2 && (
                      <div className="tb-client-panel-header">
                        <h2 className="tb-client-panel-title">{t('your_itinerary')}</h2>
                      </div>
                    )}
                    <div className="tb-slots">
                      {model.slots.map((slot, slotIndex) => (
                        <div className="tb-slot" key={slot.rootSlotKey}>
                          {slot.beforeSection &&
                            (isUiV2 ? (
                              <TripBuilderClientFlights2 label={t('flights')} title={t('your_flights')}>
                                <TripPreviewFlightsRow
                                  flights={slot.beforeSection.flights}
                                  hidePrices={hidePrices}
                                  onSelectFlight={(flightKey, isRoundTrip) =>
                                    selectBeforeFlight(slot, flightKey, isRoundTrip)
                                  }
                                />
                              </TripBuilderClientFlights2>
                            ) : (
                              <>
                                {slotIndex === 0 && (
                                  <div className="mp-itinerary-heading">
                                    <div className="mp-itinerary-heading-label text-xxs text-uppercase">{t('flights')}</div>
                                    <h2 className="mp-itinerary-heading-title">{t('your_flights')}</h2>
                                  </div>
                                )}
                                <TripPreviewOptionRow section={slot.beforeSection}>
                                  <TripPreviewFlightsRow
                                    flights={slot.beforeSection.flights}
                                    hidePrices={hidePrices}
                                    onSelectFlight={(flightKey, isRoundTrip) =>
                                      selectBeforeFlight(slot, flightKey, isRoundTrip)
                                    }
                                  />
                                </TripPreviewOptionRow>
                              </>
                            ))}

                          {slot.chainNodes.map((node, nodeIndex) => (
                            <div className="tb-slot-subsection" key={`${slot.rootSlotKey}-node-${nodeIndex}`}>
                              {node.hotelSection ? (
                                isUiV2 ? (
                                  <TripBuilderClientDestinationHotels2
                                    city={node.city}
                                    checkIn={node.checkIn}
                                    checkOut={node.checkOut}
                                    hotels={node.hotelSection.hotels}
                                    slotImageUrl={hotelImageBySlot[node.slotKey]}
                                    hidePrices={hidePrices}
                                    selectedRoomByHotel={selectedRoomByHotel}
                                    proposal={proposal}
                                    externalParam_1={proposal.externalParam_1}
                                    onSelectHotel={(hotelKey) => selectHotel(node.slotKey, hotelKey)}
                                    onSelectRoom={selectHotelRoom}
                                  />
                                ) : (
                                  <div
                                    className={
                                      'tb-hotel-group' +
                                      (expandedHotelGroups[node.slotKey] ? ' tb-hotel-group-open' : '')
                                    }
                                  >
                                    <TripPreviewHotelGroupToggleView
                                      city={node.city}
                                      dateLabel={node.dateLabel}
                                      destinationIndex={nodeIndex + 1}
                                      hotelCards={node.hotelSection.hotels}
                                      activeHotelKey={active.activeHotel[node.slotKey]}
                                      expanded={!!expandedHotelGroups[node.slotKey]}
                                      slotImageUrl={hotelImageBySlot[node.slotKey]}
                                      onToggle={() =>
                                        setExpandedHotelGroups((prev) => ({
                                          ...prev,
                                          [node.slotKey]: !prev[node.slotKey],
                                        }))
                                      }
                                    />
                                    {!!expandedHotelGroups[node.slotKey] && (
                                      <div className="tb-hotel-group-content">
                                        <TripPreviewHotelSlotImage
                                          imageUrl={hotelImageBySlot[node.slotKey]}
                                          imgClassName="tb-hotel-group-expanded-img"
                                        />
                                        <div className="tb-hotel-group-title-row flex-v-center">
                                          <span className="tb-hotel-group-title text-sm text-bold">{t('select_preferred_hotel')}</span>
                                        </div>
                                        <TripPreviewHotelsRow
                                          hotels={node.hotelSection.hotels}
                                          hidePrices={hidePrices}
                                          selectedRoomByHotel={selectedRoomByHotel}
                                          externalParam_1={proposal.externalParam_1}
                                          onSelectHotel={(hotelKey) => selectHotel(node.slotKey, hotelKey)}
                                          onSelectRoom={selectHotelRoom}
                                        />
                                      </div>
                                    )}
                                  </div>
                                )
                              ) : (
                                <>
                                  <div className="tb-slot-meta">
                                    <span className="tb-slot-city">{node.city}</span>
                                    <span className="tb-slot-date">({node.dateLabel})</span>
                                  </div>
                                  <div className="tb-slot-plus">+</div>
                                </>
                              )}
                              {node.transferSection && (
                                <TripPreviewTransfersGroup
                                  title={t('transfers_in_city', { city: node.city })}
                                  selectTitle={t('select_preferred_transfer')}
                                  emptySelectionLabel={t('no_transfer_selected')}
                                  transfers={node.transferSection.transfers}
                                  activeTransferKey={active.activeTransfer[node.slotKey]}
                                  expanded={!!expandedTransferGroups[node.slotKey]}
                                  onToggle={() =>
                                    setExpandedTransferGroups((prev) => ({
                                      ...prev,
                                      [node.slotKey]: !prev[node.slotKey],
                                    }))
                                  }
                                  hidePrices={hidePrices}
                                  onSelectTransfer={(transferKey) => selectTransfer(node.slotKey, transferKey)}
                                />
                              )}
                              {node.middleSections.map((middleSection) =>
                                isUiV2 ? (
                                  <TripBuilderClientFlights2
                                    key={middleSection.transitionKey}
                                    label={t('flights')}
                                    title={middleSection.sectionTitle || t('your_flights')}
                                  >
                                    <TripPreviewFlightsRow
                                      flights={middleSection.flights}
                                      hidePrices={hidePrices}
                                      onSelectFlight={(flightKey, isRoundTrip) =>
                                        selectMiddleFlight(
                                          slot,
                                          middleSection.transitionKey || '',
                                          flightKey,
                                          isRoundTrip,
                                        )
                                      }
                                    />
                                  </TripBuilderClientFlights2>
                                ) : (
                                  <TripPreviewOptionRow key={middleSection.transitionKey} section={middleSection}>
                                    <TripPreviewFlightsRow
                                      flights={middleSection.flights}
                                      title={middleSection.sectionTitle}
                                      hidePrices={hidePrices}
                                      onSelectFlight={(flightKey, isRoundTrip) =>
                                        selectMiddleFlight(
                                          slot,
                                          middleSection.transitionKey || '',
                                          flightKey,
                                          isRoundTrip,
                                        )
                                      }
                                    />
                                  </TripPreviewOptionRow>
                                ),
                              )}
                            </div>
                          ))}

                          {slot.afterSection &&
                            (isUiV2 ? (
                              <TripBuilderClientFlights2 label={t('flights')} title={t('your_flights')}>
                                <TripPreviewFlightsRow
                                  flights={slot.afterSection.flights}
                                  hidePrices={hidePrices}
                                  onSelectFlight={(flightKey, isRoundTrip) =>
                                    selectAfterFlight(slot, flightKey, isRoundTrip)
                                  }
                                />
                              </TripBuilderClientFlights2>
                            ) : (
                              <TripPreviewOptionRow section={slot.afterSection}>
                                <TripPreviewFlightsRow
                                  flights={slot.afterSection.flights}
                                  hidePrices={hidePrices}
                                  onSelectFlight={(flightKey, isRoundTrip) =>
                                    selectAfterFlight(slot, flightKey, isRoundTrip)
                                  }
                                />
                              </TripPreviewOptionRow>
                            ))}

                          {isUiV2 && slotIndex === model.slots.length - 1 && (
                            <TripBuilderClientRoute slots={model.slots} proposal={proposal} />
                          )}
                        </div>
                      ))}
                      {isUiV2 && (
                        <>
                          <TripBuilderClientHotelsCta2
                            onDownloadPdf={() => setIsPdfModalOpen(true)}
                            disabled={!isSelectionComplete}
                          />
                          <TripBuilderClientWhyFlyeast2 />
                        </>
                      )}
                    </div>
                  </div>
                  {!isUiV2 && (
                    <>
                      <TripBuilderClientRoute slots={model.slots} proposal={proposal} />
                      <TripBuilderClientSelection
                        model={model}
                        proposal={proposal}
                        displayDelta={model.displayTotalDelta}
                        selectedRoomByHotel={selectedRoomByHotel}
                        hotelImageBySlot={hotelImageBySlot}
                        proposalId={proposalId}
                        onSubmitSelection={onClickSaveScheme}
                        isSubmitting={saving}
                      />
                    </>
                  )}
                </>
              )}
            </div>
          </div>

          {isUiV2 && <TripBuilderClientTestimonials2 />}
          {isUiV2 && <TripBuilderClientFooter2 />}

          {isUiV2 && isPdfModalOpen && (
            <TripBuilderGetPdfModal
              close={() => setIsPdfModalOpen(false)}
              proposalId={proposalId}
              proposal={proposal}
              model={model}
              selectedRoomByHotel={selectedRoomByHotel}
              hidePrices={hidePrices}
              hotelImageBySlot={hotelImageBySlot}
              uiVersion={2}
            />
          )}
        </div>
      </div>
    </I18nextProvider>
  );
};

export default TripBuilderPage;
